import { LitElement, html } from 'lit';
import scrollMonitor from 'scrollMonitor';
import detectPrefixes from './utils/detectPrefixes';
import styles from './fit-slider.css';

const prefixes = detectPrefixes();

/**
 * private
 * translates to a given position in a given time in milliseconds
 */
function translate(slideContainer, to, duration, ease) {
  const style = slideContainer && slideContainer.style;

  if (style) {
    style[`${prefixes.transition}TimingFunction`] = ease;
    style[`${prefixes.transition}Duration`] = `${duration}ms`;
    style[prefixes.transform] = `translate3d(${to}, 0, 0)`;
  }
}

/**
 * private
 * set active class to element which is the current slide
 */
function setActiveElement(slides, currentIndex, classNameActiveSlide) {
  slides.forEach((element, index) => {
    if (element.classList.contains(classNameActiveSlide)) {
      element.classList.remove(classNameActiveSlide);
    }
  });

  slides[currentIndex].classList.add(classNameActiveSlide);
}

/**
 * Create a slider element for images
 *
 * @element fit-slider
 */
export class FitSlider extends LitElement {
  static get is() { return 'fit-slider'; }

  static get styles() { return styles; }

  render() {
    return html`
      <div id="frame" class="fit-slider__frame">
        <div id="slideContainer" class="fit-slider__slides">
          <slot id="contentNode" @click="${this.onClick}"></slot>
        </div>
      </div>
      <button id="prev" class="fit-slider__prev prev-next-button prev-next-button--prev"></button>
      <button id="next" class="fit-slider__next prev-next-button prev-next-button--next"></button>`;
  }

  static get properties() {
    return {

      /**
       * Define if the selected images should b centered.
       * Default is 'false'
       *
       * @Boolean
       */
      centerSelected: {
        type: Boolean,
        attribute: 'center-selected',
      },

      /**
       * Define if the slider should show only one image per frame.
       * Default is 'false'
       *
       * @Boolean
       */
      oneItemPerFrame: {
        type: Boolean,
        attribute: 'one-item-per-frame',
      },

      /**
       * Define if the neighbour images (still not in view) should be preloaded.
       * Default is 'false'
       *
       * @Boolean
       */
      noNeighbourPreload: {
        type: Boolean,
        attribute: 'no-neighbour-preload',
      },

      selected: {
        type: Number,
      },

      index: {
        type: Number,
      },
    };
  }

  get selected() {
    return this._selected;
  }

  set selected(selected) {
    if (!this._isSetup) {
      return this.initialIndex = selected;
    }

    this.slide(selected);
  }

  get index() {
    return this._index;
  }

  set index(index) {
    if (!this._isSetup) {
      return this.initialIndex = index;
    }

    const originalSlideSpeed = this.slideSpeed;
    this.slideSpeed = 0;
    this.slide(index);
    this.slideSpeed = originalSlideSpeed;
  }

  constructor() {
    super();
    this.centerSelected = false;
    this.oneItemPerFrame = false;
    this.noNeighbourPreload = false;

    this.classNameActiveSlide = 'fit-slider__slide--active';
    this.classNameSelectedSlide = 'fit-slider__slide--selected';
    this.slidesToScroll = 1;
    this.slideSpeed = 300;
    this.snapBackSpeed = 200;
    this.ease = 'ease';
    this.initialIndex = 0;

    this.slides = [];
    this.touchOffset = {};
    this.delta = {};

    this._isSetup = false;
    this._boundTouchmove = this.onTouchmove.bind(this);
    this._boundTouchend = this.onTouchend.bind(this);
  }

  firstUpdated() {
    this.$ = {};
    this.$.contentNode = this.shadowRoot.querySelector('#contentNode');
    this.$.frame = this.shadowRoot.querySelector('#frame');
    this.$.slideContainer = this.shadowRoot.querySelector('#slideContainer');
    this.$.prevCtrl = this.shadowRoot.querySelector('#prev');
    this.$.nextCtrl = this.shadowRoot.querySelector('#next');

    this.setup();
    this.setAttribute('initialized', 'initialized');

    this.addEventListener('will-enter-view', this._loadSlides);
  }

  setup() {
    if (this._isSetup) {
      return;
    }

    this._index = this.initialIndex;

    this.position = {
      x: 0,
    };

    this.slides = this.$.contentNode.assignedNodes({ flatten: true }).filter((node) => node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'TEMPLATE');

    if (this.$.prevCtrl) {
      this.$.prevCtrl.hidden = true;
    }

    if (this.$.nextCtrl && (this.slides.length === 1)) {
      this.$.nextCtrl.hidden = true;
    }

    this.reset();

    this.slide(this._index);

    if (this.$.prevCtrl && this.$.nextCtrl) {
      this.$.prevCtrl.addEventListener('click', this.prev.bind(this));
      this.$.nextCtrl.addEventListener('click', this.next.bind(this));
    }

    this.$.frame.addEventListener('touchstart', this.onTouchstart.bind(this), { passive: true });

    window.addEventListener('resize', this.onResize.bind(this));

    if (!this.closest('fit-modal')) {
      setTimeout(() => {
        this._scrollWatcher = scrollMonitor.create(this);

        this._scrollWatcher.on('preEnterViewport', () => {
          this._willBeInView = true;
          this.dispatchEvent(new CustomEvent('will-enter-view', { bubbles: false }));
        });

        this._scrollWatcher.on('enterViewport', () => {
          this._willBeInView = false;
          this._isInView = true;
        });

        this._scrollWatcher.on('exitViewport', () => {
          this._willBeInView = true;
          this._isInView = false;
        });

        this._scrollWatcher.on('preExitViewport', () => {
          this._willBeInView = false;
        });
      }, 1);
    } else {
      this._isInView = true;
      this._loadSlides();
    }

    this._isSetup = true;
  }

  reset() {
    requestIdleCallback(() => {
      this.frameWidth = this.$.frame.clientWidth;
    });

    this.oneItemPerFrame ? this._sizeSlidesToFrameWidth() : this._sizeSlidesToFrameHeight();

    const nextOffset = this._determineOffset(this._index);
    translate(this.$.slideContainer, `${nextOffset}%`, 0, null);
    this.position.x = nextOffset;

    if (this.classNameActiveSlide) {
      setActiveElement(this.slides, this._index, this.classNameActiveSlide);
    }
  }

  /**
   * public
   * prev function: called on clickhandler
   */
  prev(event) {
    event instanceof Event && event.preventDefault();
    this.slide(false, false);
  }

  /**
   * public
   * next function: called on clickhandler
   */
  next(event) {
    event instanceof Event && event.preventDefault();
    this.slide(false, true);
  }

  /**
   * slidefunction called by prev, next & touchend
   *
   * determine nextIndex and slide to next postion
   * under restrictions of the defined options
   *
   * @direction  {boolean}
   */
  slide(nextIndex, direction = null) {
    /**
     * Reset control classes
     */
    if (this.$.prevCtrl) {
      this.$.prevCtrl.hidden = false;
    }
    if (this.$.nextCtrl) {
      this.$.nextCtrl.hidden = false;
    }

    if (typeof nextIndex !== 'number') {
      if (direction) {
        nextIndex = this._index + this.slidesToScroll;
      } else {
        nextIndex = this._index - this.slidesToScroll;
      }
    }

    this._selected = Math.min(Math.max(nextIndex, 0), this.slides.length - 1);

    this._loadSlides();

    this.dispatchEvent(new CustomEvent('fit-slider-selected-changed', { detail: this._selected, bubbles: false, composed: true }));

    if (this.classNameSelectedSlide) {
      setActiveElement(this.slides, this._selected, this.classNameSelectedSlide);
    }

    const currentPageSlides = this._getPageSlides(this._index);

    if (!currentPageSlides.includes(this.slides[this._selected])) {
      nextIndex = this.slides.indexOf(this._getPageSlides(this._selected)[0]);
      const nextOffset = this._determineOffset(nextIndex);
      translate(this.$.slideContainer, `${nextOffset}%`, this.slideSpeed, this.ease);
      this.position.x = nextOffset;

      /**
       * update the index with the nextIndex only if
       * the offset of the nextIndex is in the range of the maxOffset
       */
      if (this.slides[nextIndex].flexOffsetLeft <= 100) {
        this._index = nextIndex;
      }

      if (this.classNameActiveSlide) {
        setActiveElement(this.slides, this._index, this.classNameActiveSlide);
      }
    }

    /**
     * update classes for next and prev arrows
     * based on user settings
     */
    if (this.$.prevCtrl && this._index === 0) {
      this.$.prevCtrl.hidden = true;
    }

    const nextPageSlides = this._getPageSlides(this._index, 1);
    if (this.$.nextCtrl && nextPageSlides.length === 0) {
      this.$.nextCtrl.hidden = true;
    }
  }

  onTransitionEnd() {
    if (this.transitionEndCallback) {
      this.transitionEndCallback();

      this.transitionEndCallback = undefined;
    }
  }

  onTouchstart(event) {
    const touches = event.touches ? event.touches[0] : event;

    this.$.frame.addEventListener('touchmove', this._boundTouchmove, { passive: true });
    this.$.frame.addEventListener('touchend', this._boundTouchend);

    const { pageX, pageY } = touches;

    this.touchOffset = {
      x: pageX,
      y: pageY,
      time: Date.now(),
    };

    this.isScrolling = undefined;

    this.delta = {};
  }

  onTouchmove(event) {
    const touches = event.touches ? event.touches[0] : event;
    const { pageX, pageY } = touches;

    this.delta = {
      x: pageX - this.touchOffset.x,
      y: pageY - this.touchOffset.y,
    };

    if (typeof this.isScrolling === 'undefined') {
      this.isScrolling = !!(this.isScrolling || Math.abs(this.delta.x) < Math.abs(this.delta.y));
    }

    if (!this.isScrolling && this.touchOffset) {
      translate(this.$.slideContainer, `calc(${this.position.x}% + ${this.delta.x}px)`, 0, null);
    }
  }

  onTouchend(event) {
    /**
     * time between touchstart and touchend in milliseconds
     * @duration {number}
     */
    const duration = this.touchOffset ? Date.now() - this.touchOffset.time : undefined;

    /**
     * is valid if:
     *
     * -> swipe attempt time is over 300 ms
     * and
     * -> swipe distance is greater than 25px
     * or
     * -> swipe distance is more then a third of the swipe area
     *
     * @isValidSlide {Boolean}
     */
    const isValid = Number(duration) < 300 && Math.abs(this.delta.x) > 25 || Math.abs(this.delta.x) > this.frameWidth / 3;

    /**
     * is out of bounds if:
     *
     * -> index is 0 and delta x is greater than 0
     * or
     * -> index is the last slide and delta is smaller than 0
     *
     * @isOutOfBounds {Boolean}
     */
    const isOutOfBounds = !this._index && this.delta.x > 0 || this._index === this.slides.length - 1 && this.delta.x < 0;

    const direction = this.delta.x < 0;

    if (!this.isScrolling) {
      if (isValid && !isOutOfBounds) {
        this.slide(false, direction);
      } else {
        translate(this.$.slideContainer, `${this.position.x}%`, this.snapBackSpeed);
      }
    }

    this.touchOffset = undefined;

    /**
     * remove eventlisteners after swipe attempt
     */
    this.$.frame.removeEventListener('touchmove', this._boundTouchmove);
    this.$.frame.removeEventListener('touchend', this._boundTouchend);
  }

  onResize(event) {
    if (this.frameWidth !== this.$.frame.clientWidth) {
      this.reset();
    }
  }

  onClick(event) {
    const path = event.path || (event.composedPath && event.composedPath());
    const contentNodeIndex = path.indexOf(this.$.contentNode);
    const slide = path.slice(0, contentNodeIndex).reverse().find((node) => node.tagName !== 'SLOT');
    const slideIndex = this.slides.indexOf(slide);

    if (slideIndex >= 0) {
      this.selected = slideIndex;
    }
  }

  _sizeSlidesToFrameWidth() {
    this.$.slideContainer.style.width = `${this.slides.length * 100}%`;

    const flexBasis = (Math.round(1 / this.slides.length * 1000000) / 10000);
    let flexOffsetLeft = 0;

    this.slides.forEach((slide) => {
      slide.style['flex-basis'] = `${flexBasis}%`;
      if (slide.originalSlideWidth === undefined) {
        slide.originalSlideWidth = slide.style.width || '';
      }
      slide.style.width = `${flexBasis}%`;
      if (slide.originalSlideHeight === undefined) {
        slide.originalSlideHeight = slide.style.height || '';
      }
      slide.style.height = 'auto';

      slide.flexOffsetLeft = flexOffsetLeft;
      flexOffsetLeft += flexBasis;
    });
    this.slidesToScroll = 1;
  }

  _sizeSlidesToFrameHeight() {
    const frameWidth = this.$.frame.clientWidth;
    const slideDimensions = new Map();

    let totalWidth = 0;
    let totalHeight = 0;

    this.slides.forEach((slide) => {
      !slide.originalSlideWidth && (slide.originalSlideWidth = slide.offsetWidth);
      !slide.originalSlideHeight && (slide.originalSlideHeight = slide.offsetHeight);

      const aspectRatio = (slide.getAttribute('width') || slide.originalSlideWidth)
        / (slide.getAttribute('height') || slide.originalSlideHeight);

      totalHeight += slide.originalSlideHeight;

      slideDimensions.set(slide, {
        width: slide.originalSlideWidth,
        height: slide.originalSlideHeight,
        aspectRatio
      });
    });

    const scale = this.$.frame.clientHeight / (totalHeight / this.slides.length);

    slideDimensions.forEach((dimensions) => {
      dimensions.width = Math.min(
        Math.floor(scale * dimensions.height * dimensions.aspectRatio),
        Math.floor(scale * dimensions.width),
        frameWidth
      );
      totalWidth += dimensions.width;
    });

    const scaledSlideContainerWidth = Math.round(totalWidth / frameWidth * 1000000) / 10000;

    if (scaledSlideContainerWidth) {
      this.$.slideContainer.style.width = `${scaledSlideContainerWidth}%`;

      let flexOffsetLeft = 0;
      let accumulatedWidth = 0;
      let slidesInFrame = 0;
      slideDimensions.forEach((dimensions, slide) => {
        const flexBasis = Math.round((dimensions.width) / totalWidth * 1000000) / 10000;

        slide.style['flex-basis'] = `${flexBasis}%`;
        slide.style.width = `${flexBasis}%`;
        slide.style.height = 'auto';

        slide.flexOffsetLeft = flexOffsetLeft;
        flexOffsetLeft += flexBasis;

        accumulatedWidth += dimensions.width;

        if (accumulatedWidth < frameWidth) {
          slidesInFrame++;
        }
      });

      this.slidesToScroll = this.centerSelected ? 1 : slidesInFrame;
    }
  }

  _determineOffset(index) {
    const frameWidth = (100 / parseFloat(this.$.slideContainer.style.width) * 100) || 1; // so many 100, because percent, you know?
    const lastSlide = this.slides[this.slides.length - 1];
    const currentSlide = this.slides[index];
    const currentSlideWidth = parseFloat(currentSlide.style.width);
    const maxOffset = lastSlide.flexOffsetLeft - frameWidth + parseFloat(lastSlide.style.width);

    let offset = 0;
    if (this.slidesToScroll === 1 && this.centerSelected) {
      offset = Math.min(Math.max((currentSlide.flexOffsetLeft - (frameWidth / 2) + (currentSlideWidth / 2)) * -1, maxOffset * -1), offset);
    } else {
      offset = Math.min(Math.max(currentSlide.flexOffsetLeft * -1, maxOffset * -1), offset);
    }

    return offset;
  }

  _loadSlides(index) {
    if (!this._isVisible()) {
      return;
    }

    (this.noNeighbourPreload ? [0] : [0, 1, -1]).forEach((relativeIndex) => {
      const slides = this.centerSelected ? this._getVisiblePageSlides(this._selected, relativeIndex) : this._getPageSlides(this._selected, relativeIndex);
      slides.forEach((slide) => { this._loadItem(slide); });
    });
  }

  _getPageSlides(currentSelection, relativePage = 0) {
    if (this.slides[currentSelection] === undefined) {
      return [];
    }

    const currentPage = Math.floor(currentSelection / this.slidesToScroll);
    const desiredPage = currentPage + relativePage;
    const maxPages = Math.ceil(this.slides.length / this.slidesToScroll);

    if (desiredPage < 0 || desiredPage > maxPages) {
      return [];
    }

    const slidesStart = desiredPage * this.slidesToScroll;
    const slidesEnd = slidesStart + this.slidesToScroll;
    return this.slides.slice(slidesStart, slidesEnd);
  }

  _getVisiblePageSlides(currentSelection, relativePage = 0) {
    const frameWidth = (100 / parseFloat(this.$.slideContainer.style.width) * 100) || 1; // so many 100, because percent, you know?
    const currentSlide = this.slides[currentSelection];
    const currentPage = Math.floor((currentSlide.flexOffsetLeft + parseFloat(currentSlide.style.width)) / frameWidth);

    if (currentPage + relativePage <= 0 || currentPage * frameWidth + relativePage * frameWidth > 100) {
      return [];
    }

    const leftMaxOffset = currentPage * frameWidth + (relativePage * frameWidth);
    const rightMaxOffset = (currentPage + 1) * frameWidth + (relativePage * frameWidth);

    return this.slides.filter((slide) => slide.flexOffsetLeft >= leftMaxOffset && slide.flexOffsetLeft < rightMaxOffset);
  }

  _loadItem(node) {
    let nodes = [];
    if (node.tagName === 'IFRAME' || node.tagName === 'IMG') {
      nodes.push(node);
    } else {
      nodes = node.querySelectorAll('iframe[lazy-load-src],img[lazy-load-src],img[lazy-load-srcset]');
    }

    let someLoaded = false;
    nodes.forEach((node) => {
      if (node.tagName === 'IFRAME' && node.hasAttribute('lazy-load-src')) {
        node.src = node.getAttribute('lazy-load-src');
        someLoaded = true;
      }
      if (node.tagName === 'IMG' && node.hasAttribute('lazy-load-src')) {
        node.src = node.getAttribute('lazy-load-src');
        someLoaded = true;
      }
      if (node.tagName === 'IMG' && node.hasAttribute('lazy-load-srcset')) {
        node.srcset = node.getAttribute('lazy-load-srcset');
        someLoaded = true;
      }
    });

    return someLoaded;
  }

  _isVisible() {
    return (this._willBeInView || this._isInView) && window.getComputedStyle(this, null).getPropertyValue('display') !== 'none';
  }
}

customElements.define(FitSlider.is, FitSlider);
